import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { courierAPI, ordersAPI } from "../../utils/api";
import {
  Package,
  MapPin,
  CheckCircle,
  XCircle,
  Truck,
  Clock,
  ChevronRight,
  Phone,
  User,
  Loader2,
  AlertCircle,
  Navigation,
  DollarSign,
  Weight,
  Calendar,
} from "lucide-react";
import toast from "react-hot-toast";

const statusConfig = {
  assigned: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: Truck,
    label: "Assigned",
    action: "Pick Up",
    actionColor: "btn-primary",
  },
  picked_up: {
    color: "bg-violet-100 text-violet-800 border-violet-200",
    icon: Package,
    label: "Picked Up",
    action: "Deliver",
    actionColor: "btn-primary",
  },
  in_transit: {
    color: "bg-cyan-100 text-cyan-800 border-cyan-200",
    icon: Navigation,
    label: "In Transit",
    action: "Deliver",
    actionColor: "btn-primary",
  },
  delivered: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: CheckCircle,
    label: "Delivered",
  },
  failed: {
    color: "bg-rose-100 text-rose-800 border-rose-200",
    icon: XCircle,
    label: "Failed",
  },
};

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Invalid Date";
  }
};

const CourierDeliveries = () => {
  const [activeTab, setActiveTab] = useState("active");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [notes, setNotes] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: activeOrders,
    isLoading: activeLoading,
    error: activeError,
  } = useQuery({
    queryKey: ["courierActiveOrders"],
    queryFn: () => courierAPI.assignedOrders(),
    retry: 2,
  });

  const { data: historyOrders, isLoading: historyLoading } = useQuery({
    queryKey: ["courierHistory"],
    queryFn: () => courierAPI.deliveryHistory(30),
    retry: 2,
  });

  const pickupMutation = useMutation({
    mutationFn: (id) => courierAPI.markPickedUp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courierActiveOrders"] });
      toast.success("Order marked as picked up");
      setShowActionModal(false);
      setSelectedOrder(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update status");
    },
  });

  const deliverMutation = useMutation({
    mutationFn: (id) => courierAPI.markDelivered(id, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courierActiveOrders"] });
      queryClient.invalidateQueries({ queryKey: ["courierHistory"] });
      toast.success("Order marked as delivered");
      setShowActionModal(false);
      setSelectedOrder(null);
      setNotes("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update status");
    },
  });

  const failMutation = useMutation({
    mutationFn: (id) => courierAPI.markFailed(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courierActiveOrders"] });
      queryClient.invalidateQueries({ queryKey: ["courierHistory"] });
      toast.success("Order marked as failed");
      setShowActionModal(false);
      setSelectedOrder(null);
      setNotes("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update status");
    },
  });

  const handleAction = () => {
    if (!selectedOrder) return;
    switch (actionType) {
      case "pickup":
        pickupMutation.mutate(selectedOrder.id);
        break;
      case "deliver":
        deliverMutation.mutate(selectedOrder.id);
        break;
      case "fail":
        failMutation.mutate(selectedOrder.id);
        break;
    }
  };

  const openActionModal = (order, action) => {
    setSelectedOrder(order);
    setActionType(action);
    setNotes("");
    setShowActionModal(true);
  };

  const openDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const active = activeOrders?.data?.data || [];
  const history = historyOrders?.data?.data || [];
  const isLoading = activeLoading || historyLoading;
  const currentOrders = activeTab === "active" ? active : history;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    );
  }

  if (activeError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="font-medium">Failed to load deliveries</p>
        <button
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["courierActiveOrders"] })
          }
          className="mt-3 btn-secondary text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Deliveries</h1>
          <p className="text-gray-500 mt-1">
            Manage your delivery assignments and history
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl w-fit">
        {["active", "history"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab === "active" ? "Active" : "History"} (
            {tab === "active" ? active.length : history.length})
          </button>
        ))}
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {currentOrders.length === 0 ? (
          <div className="card text-center py-16">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 font-medium text-lg">
              No {activeTab} deliveries
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {activeTab === "active"
                ? "New assignments will appear here"
                : "Completed deliveries will appear here"}
            </p>
          </div>
        ) : (
          currentOrders.map((order) => {
            const StatusIcon = statusConfig[order.status]?.icon || Truck;
            const config = statusConfig[order.status];
            return (
              <div
                key={order.id}
                className="card hover:shadow-lg transition-all duration-200 border border-gray-100"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-2.5 rounded-lg ${config?.color || "bg-gray-100"}`}
                    >
                      <StatusIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                          {order.tracking_number}
                        </span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${config?.color || "bg-gray-100"}`}
                        >
                          {config?.label || order.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">
                      EGP {formatMoney(order.total_amount)}
                    </p>
                    <p className="text-xs text-gray-400">
                      Courier fee: EGP {formatMoney(order.courier_fee)}
                    </p>
                  </div>
                </div>

                {/* Route */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                      <MapPin className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Pickup
                      </p>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {order.pickup_address}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {order.recipient_name} • {order.recipient_phone}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                      <Navigation className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Delivery
                      </p>
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {order.delivery_address}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {order.delivery_type} • {order.package_type}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Package Info */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center bg-gray-50 px-2 py-1 rounded-lg">
                      <Weight className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                      {order.weight}kg
                    </span>
                    <span className="flex items-center bg-gray-50 px-2 py-1 rounded-lg">
                      <Package className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                      {order.package_type}
                    </span>
                    <span className="flex items-center bg-gray-50 px-2 py-1 rounded-lg">
                      <DollarSign className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                      {order.payment_method}
                    </span>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => openDetails(order)}
                      className="btn-secondary text-sm py-2 px-4"
                    >
                      Details
                    </button>
                    {activeTab === "active" && config?.action && (
                      <button
                        onClick={() =>
                          openActionModal(
                            order,
                            actionTypeForStatus(order.status),
                          )
                        }
                        className={`${config.actionColor} text-sm py-2 px-4 flex items-center`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1.5" />
                        {config.action}
                      </button>
                    )}
                    {activeTab === "active" &&
                      (order.status === "in_transit" ||
                        order.status === "picked_up") && (
                        <button
                          onClick={() => openActionModal(order, "fail")}
                          className="btn-secondary text-sm py-2 px-4 text-rose-600 hover:bg-rose-50 border-rose-200"
                        >
                          <XCircle className="w-4 h-4 mr-1.5" />
                          Failed
                        </button>
                      )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {actionType === "pickup" && "Confirm Pickup"}
              {actionType === "deliver" && "Confirm Delivery"}
              {actionType === "fail" && "Mark as Failed"}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Order:{" "}
              <span className="font-mono font-medium text-gray-700">
                {selectedOrder?.tracking_number}
              </span>
            </p>
            <div className="bg-gray-50 p-3 rounded-lg mb-4 text-sm">
              <p className="text-gray-600">
                <span className="font-medium">From:</span>{" "}
                {selectedOrder?.pickup_address}
              </p>
              <p className="text-gray-600 mt-1">
                <span className="font-medium">To:</span>{" "}
                {selectedOrder?.delivery_address}
              </p>
            </div>

            {(actionType === "deliver" || actionType === "fail") && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {actionType === "fail"
                    ? "Reason for failure"
                    : "Delivery notes"}{" "}
                  (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input-field w-full"
                  rows={3}
                  placeholder={
                    actionType === "fail"
                      ? "Customer not available, wrong address..."
                      : "Left at front door, handed to recipient..."
                  }
                />
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowActionModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={
                  pickupMutation.isPending ||
                  deliverMutation.isPending ||
                  failMutation.isPending
                }
                className={`btn-primary disabled:opacity-50 ${actionType === "fail" ? "bg-rose-600 hover:bg-rose-700" : ""}`}
              >
                {pickupMutation.isPending ||
                deliverMutation.isPending ||
                failMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Order Details
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">Tracking Number</span>
                <span className="font-mono font-semibold text-gray-900">
                  {selectedOrder.tracking_number}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Weight</p>
                  <p className="font-semibold text-gray-900">
                    {selectedOrder.weight}kg
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Dimensions</p>
                  <p className="font-semibold text-gray-900">
                    {selectedOrder.dimensions || "N/A"}
                  </p>
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase mb-1">Notes</p>
                <p className="text-sm text-gray-700">
                  {selectedOrder.notes || "No notes"}
                </p>
              </div>
              <div className="border-t border-gray-100 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">
                    EGP {formatMoney(selectedOrder.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-gray-500">Delivery Fee</span>
                  <span className="font-medium">
                    EGP {formatMoney(selectedOrder.delivery_fee)}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-gray-500">Tax</span>
                  <span className="font-medium">
                    EGP {formatMoney(selectedOrder.tax)}
                  </span>
                </div>
                <div className="flex justify-between text-base font-bold mt-3 pt-3 border-t border-gray-100">
                  <span>Total</span>
                  <span>EGP {formatMoney(selectedOrder.total_amount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function actionTypeForStatus(status) {
  if (status === "assigned") return "pickup";
  if (status === "picked_up" || status === "in_transit") return "deliver";
  return "deliver";
}

export default CourierDeliveries;
